/**
 * @fileoverview A component that displays the status of a participant.
 * It uses the `States` enum to determine the status and the `statusOptions` array to display the status.
 *
 * @example
 * <Status status={States.review} />
 */

import ExcludedIcon from '../assets/stages/excluded.svg'
import InvitedIcon from '../assets/stages/invited.svg'
import NotQualifiedIcon from '../assets/stages/notQualified.svg'
import QualifiedIcon from '../assets/stages/qualified.svg'
import RandomisedIcon from '../assets/stages/randomised.svg'
import ReviewIcon from '../assets/stages/review.svg'
import { States } from '../config'

export const statusOptions = [
    {
        name: 'Requiring review',
        value: States.review,
        selectedIcon: <ReviewIcon className="w-5 h-5 mr-2" />,
        listIcon: <ReviewIcon className="w-5 h-5" />,
        tooltip: (
            <div className="w-40">
                <span className="text-xs">
                    Participants requiring additional review to determine suitability for inclusion.
                </span>
                <p>
                    <a
                        className="text-xs text-indigo-400 underline underline-offset-2"
                        href="https://help.novoic.com/en/articles/8490035-what-does-requiring-review-mean"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more →
                    </a>
                </p>
            </div>
        ),
    },
    {
        name: 'Lower priority',
        value: States.not_qualified,
        selectedIcon: <NotQualifiedIcon className="w-5 h-5 mr-2" />,
        listIcon: <NotQualifiedIcon className="w-5 h-5" />,
        tooltip: (
            <div className="w-40">
                <span className="text-xs">
                    Inbox of participants not meeting initial pre-qualification criteria.
                </span>
                <p>
                    <a
                        className="text-xs text-indigo-400 underline underline-offset-2"
                        href="https://help.novoic.com/en/articles/8490068-what-does-lower-priority-mean"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more →
                    </a>
                </p>
            </div>
        ),
    },
    {
        name: 'High priority',
        value: States.qualified,
        selectedIcon: <QualifiedIcon className="w-5 h-5 mr-2" />,
        listIcon: <QualifiedIcon className="w-5 h-5" />,
        tooltip: (
            <div className="w-40">
                <span className="text-xs">
                    Inbox of pre-qualified participants meeting initial study requirements.
                </span>
                <p>
                    <a
                        className="text-xs text-indigo-400 underline underline-offset-2"
                        href="https://help.novoic.com/en/articles/8490059-what-does-high-priority-mean"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more →
                    </a>
                </p>
            </div>
        ),
    },
    {
        name: 'Excluded',
        value: States.excluded,
        selectedIcon: <ExcludedIcon className="w-5 h-5 mr-2" />,
        listIcon: <ExcludedIcon className="w-5 h-5" />,
        tooltip: (
            <div className="w-40">
                <span className="text-xs">
                    Participants determined to be ineligible, unavailable, or unsuitable for
                    inclusion.
                </span>
                <p>
                    <a
                        className="text-xs text-indigo-400 underline underline-offset-2"
                        href="https://help.novoic.com/en/articles/8490081-what-does-excluded-mean"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more →
                    </a>
                </p>
            </div>
        ),
    },
    {
        name: 'Invited',
        value: States.invited,
        selectedIcon: <InvitedIcon className="w-5 h-5 mr-2" />,
        listIcon: <InvitedIcon className="w-5 h-5" />,
        tooltip: (
            <div className="w-40">
                <span className="text-xs">
                    Participants meeting all screening criteria and scheduled for more in-depth
                    assessment.
                </span>
                <p>
                    <a
                        className="text-xs text-indigo-400 underline underline-offset-2"
                        href="https://help.novoic.com/en/articles/8490072-what-does-invited-mean"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more →
                    </a>
                </p>
            </div>
        ),
    },
    {
        name: 'Randomised',
        value: States.randomised,
        selectedIcon: <RandomisedIcon className="w-5 h-5 mr-2" />,
        listIcon: <RandomisedIcon className="w-5 h-5" />,
        tooltip: (
            <div className="w-40">
                <span className="text-xs">Participants randomised into clinical trials.</span>
                <p>
                    <a
                        className="text-xs text-indigo-400 underline underline-offset-2"
                        href="https://help.novoic.com/en/articles/8490078-what-does-randomised-mean"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Learn more →
                    </a>
                </p>
            </div>
        ),
    },
]

/**
 * @interface StatusProps
 * @description Props for the Status component.
 * @property {States} status - The status of the participant.
 */
interface StatusProps {
    status: States
}

/**
 * @function Status
 * @description Component for displaying the status of a participant.
 * @param {StatusProps} props - The props for the Status component.
 * @returns {JSX.Element | null} - The Status component.
 */
export function Status(props: StatusProps) {
    const current = statusOptions.find(({ value }) => value === props.status)
    if (!current) {
        return <span className="text-gray-400">n/a</span>
    }
    return (
        <div className="flex items-center cursor-pointer text-left transition-all">
            {current?.listIcon || null}
            <div className="ml-2">
                <div className="flex text-sm font-medium text-gray-900">
                    <span className="whitespace-nowrap">{current?.name}</span>
                </div>
            </div>
        </div>
    )
}
